/**
 * Session Lock Hook
 * Sprint 12 REG-015 - HIPAA Workstation Security
 *
 * Features:
 * - Manual lock via Ctrl+L shortcut
 * - Auto-lock after configurable inactivity
 * - Session state preservation
 * - Lock screen overlay management
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@shared/context/AuthContext";
import apiClient from "@shared/services/apiClient";

interface UseSessionLockOptions {
  inactivityTimeout?: number; // Minutes of inactivity before auto-lock
  enabled?: boolean; // Master switch for lock functionality
  enableKeyboardShortcut?: boolean; // Register Ctrl+L listener internally
}

const SESSION_LOCK_STORAGE_KEY = "nervedx.sessionLock";

interface PersistedSessionLock {
  locked: boolean;
  autoLocked: boolean;
  userId: string;
  lockedAt: string;
}

function readPersistedSessionLock(): PersistedSessionLock | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(SESSION_LOCK_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedSessionLock>;
    if (
      parsed?.locked === true &&
      typeof parsed.userId === "string" &&
      parsed.userId.trim().length > 0
    ) {
      return {
        locked: true,
        autoLocked: Boolean(parsed.autoLocked),
        userId: parsed.userId,
        lockedAt:
          typeof parsed.lockedAt === "string"
            ? parsed.lockedAt
            : new Date().toISOString(),
      };
    }
  } catch {
    // Treat corrupt storage as unlocked; the server session still protects auth.
  }
  return null;
}

function persistSessionLock(userId: string, autoLocked: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      SESSION_LOCK_STORAGE_KEY,
      JSON.stringify({
        locked: true,
        autoLocked,
        userId,
        lockedAt: new Date().toISOString(),
      } satisfies PersistedSessionLock),
    );
  } catch {
    // Storage can be unavailable; runtime lock still applies for this tab.
  }
}

function clearPersistedSessionLock(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(SESSION_LOCK_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export const useSessionLock = (options: UseSessionLockOptions = {}) => {
  const {
    inactivityTimeout = 15, // Default 15 minutes
    enabled = true,
    enableKeyboardShortcut = true,
  } = options;

  const { user, loading, authCheckLoading } = useAuth();
  const [isLocked, setIsLocked] = useState(
    () => Boolean(readPersistedSessionLock()),
  );
  const [autoLocked, setAutoLocked] = useState(
    () => readPersistedSessionLock()?.autoLocked ?? false,
  );
  const lastActivityRef = useRef<number>(Date.now());
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Lock session manually or automatically
  const lockSession = useCallback(
    (auto: boolean = false) => {
      if (!enabled || !user) return;

      setIsLocked(true);
      setAutoLocked(auto);
      persistSessionLock(user.id, auto);

      // REG-015: Persist server-side lock state. The backend audits this event
      // and rejects protected API calls until unlock verification clears it.
      apiClient
        .post("/users/lock-session", { autoLocked: auto })
        .catch(() => {}); // Don't block on audit failure
    },
    [enabled, user],
  );

  // Unlock session (handled by LockScreen component)
  const unlockSession = useCallback(() => {
    clearPersistedSessionLock();
    setIsLocked(false);
    setAutoLocked(false);
    lastActivityRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (!enabled) {
      clearPersistedSessionLock();
      setIsLocked(false);
      setAutoLocked(false);
      return;
    }

    const persisted = readPersistedSessionLock();
    if (user) {
      if (persisted?.userId === user.id) {
        setIsLocked(true);
        setAutoLocked(persisted.autoLocked);
      } else if (persisted) {
        clearPersistedSessionLock();
        setIsLocked(false);
        setAutoLocked(false);
      }
      return;
    }

    if (!loading && !authCheckLoading) {
      clearPersistedSessionLock();
      setIsLocked(false);
      setAutoLocked(false);
    }
  }, [authCheckLoading, enabled, loading, user]);

  useEffect(() => {
    if (!enabled || !user) return;

    const handleServerSessionLocked = () => {
      setIsLocked(true);
      setAutoLocked(true);
      persistSessionLock(user.id, true);
    };

    window.addEventListener("app:session-locked", handleServerSessionLocked);
    return () => {
      window.removeEventListener("app:session-locked", handleServerSessionLocked);
    };
  }, [enabled, user]);

  // Track user activity
  const recordActivity = useCallback(() => {
    if (!enabled || isLocked) return;
    lastActivityRef.current = Date.now();
  }, [enabled, isLocked]);

  // Check for inactivity
  useEffect(() => {
    if (!enabled || !user) return;

    const checkInactivity = () => {
      const now = Date.now();
      const inactiveFor = (now - lastActivityRef.current) / 1000 / 60; // Minutes

      if (inactiveFor >= inactivityTimeout && !isLocked) {
        lockSession(true); // Auto-lock
      }
    };

    // Check every 30 seconds
    inactivityTimerRef.current = setInterval(checkInactivity, 30000);

    return () => {
      if (inactivityTimerRef.current) {
        clearInterval(inactivityTimerRef.current);
      }
    };
  }, [enabled, user, inactivityTimeout, isLocked, lockSession]);

  // Register activity listeners
  useEffect(() => {
    if (!enabled) return;

    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    events.forEach((event) => {
      document.addEventListener(event, recordActivity);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, recordActivity);
      });
    };
  }, [enabled, recordActivity]);

  // Ctrl+L shortcut to lock
  useEffect(() => {
    if (!enabled || !enableKeyboardShortcut) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === "l") {
        e.preventDefault();
        lockSession(false); // Manual lock
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enabled, enableKeyboardShortcut, lockSession]);

  return {
    isLocked,
    autoLocked,
    lockSession,
    unlockSession,
  };
};
